import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
    type ReactNode,
} from 'react';
import type {
    GetTaskListParamsType,
    PaginatorType,
    TaskType,
} from '@econtractor/types';
import { useTranslation } from 'react-i18next';
import * as apiTask from 'api/apiTask';
import { useAuth } from 'context/AuthContext';
import { useToast } from 'context/ToastContext';
import { getStoredListPageSize } from 'utils/helpers/list/listPageSize';
import { canViewAllTasks } from 'utils/helpers/access/hasUserAccess';

export const TASKS_PAGE_SIZE_KEY = 'tasks-list';

export interface TaskContextType {
    tasks: TaskType[] | null;
    paginator: PaginatorType | null;
    showSpinner: boolean;
    page: number;
    setPage: (page: number) => void;
    onPage: number;
    setOnPage: (onPage: number) => void;
    tasksOrder: string;
    setTasksOrder: (order: string) => void;
    tasksDesc: boolean;
    setTasksDesc: (desc: boolean) => void;
    tasksStatus: number[] | null;
    setTasksStatus: (status: number[] | null) => void;
    tasksType: number[] | null;
    setTasksType: (taskType: number[] | null) => void;
    /** ID виконавців (масив; UI зараз передає 0–1 елемент) */
    tasksExecutor: number[] | null;
    setTasksExecutor: (executorIds: number[] | null) => void;
    /** ID контролерів (масив; UI зараз передає 0–1 елемент) */
    tasksController: number[] | null;
    setTasksController: (controllerIds: number[] | null) => void;
    /** ID власників / авторів (масив; UI поки не використовує) */
    tasksOwner: number[] | null;
    setTasksOwner: (ownerIds: number[] | null) => void;
    /** Фільтр заявки (коли контекст не зафіксований через props) */
    filterLeadId: number | null;
    setFilterLeadId: (leadId: number | null) => void;
    /** Фільтр об'єкта (коли контекст не зафіксований через props) */
    filterObjectId: number | null;
    setFilterObjectId: (objectId: number | null) => void;
    /** Лише задачі без прив’язки до заявки / об’єкта */
    onlyPersonal: boolean;
    setOnlyPersonal: (value: boolean) => void;
    /** Лише задачі, де поточний користувач — власник, контролер або виконавець */
    onlyMine: boolean;
    setOnlyMine: (value: boolean) => void;
    /** Зафіксований scope (OffTasks тощо) */
    leadId: number | null;
    objectId: number | null;
    /** Чи можна фільтрувати за заявкою / об'єктом / особистими */
    canFilterRelated: boolean;
    listRefreshNonce: number;
    triggerListRefresh: () => void;
    triggerListReset: () => void;
    fetchTasks: (force?: boolean) => Promise<void>;
}

const TaskContext = createContext<TaskContextType | null>(null);

export const useTask = (): TaskContextType => {
    const ctx = useContext(TaskContext);
    if (!ctx) {
        throw new Error('useTask must be used within TaskProvider');
    }
    return ctx;
};

export interface TaskProviderProps {
    children: ReactNode;
    leadId?: number | null;
    objectId?: number | null;
    /** Коли false — не фетчить (наприклад, offcanvas закритий) */
    enabled?: boolean;
    /**
     * Не показувати прострочені закриті задачі (Прийнята / Скасована / видалена).
     * Прострочені в роботі та на затвердженні — лишаються.
     */
    noShowOlderTask?: boolean;
    /** Не показувати видалені задачі */
    noShowDeletedTask?: boolean;
    /** Початковий розмір сторінки (для канбану тощо) */
    defaultOnPage?: number;
    /** Початковий фільтр статусів (наприклад з localStorage) */
    initialTasksStatus?: number[] | null;
    /** Початковий фільтр типів (наприклад з localStorage) */
    initialTasksType?: number[] | null;
    initialTasksExecutor?: number[] | null;
    initialTasksController?: number[] | null;
    initialTasksOwner?: number[] | null;
    initialFilterLeadId?: number | null;
    initialFilterObjectId?: number | null;
    initialOnlyPersonal?: boolean;
    /** Початкове значення «лише свої»; за замовчуванням увімкнено */
    initialOnlyMine?: boolean;
}

export const TaskProvider = ({
    children,
    leadId = null,
    objectId = null,
    enabled = true,
    noShowOlderTask = false,
    noShowDeletedTask = false,
    defaultOnPage,
    initialTasksStatus = null,
    initialTasksType = null,
    initialTasksExecutor = null,
    initialTasksController = null,
    initialTasksOwner = null,
    initialFilterLeadId = null,
    initialFilterObjectId = null,
    initialOnlyPersonal = false,
    initialOnlyMine = true,
}: TaskProviderProps) => {
    const { t } = useTranslation(['common']);
    const { auth } = useAuth();
    const { showToast } = useToast();

    const [tasks, setTasks] = useState<TaskType[] | null>(null);
    const [paginator, setPaginator] = useState<PaginatorType | null>(null);
    const [showSpinner, setShowSpinner] = useState(false);
    const [page, setPage] = useState(1);
    const [onPage, setOnPage] = useState(() =>
        defaultOnPage != null
            ? defaultOnPage
            : getStoredListPageSize(auth?.user?.ID, TASKS_PAGE_SIZE_KEY),
    );
    const [tasksOrder, setTasksOrder] = useState('');
    const [tasksDesc, setTasksDesc] = useState(false);
    const [tasksStatus, setTasksStatus] = useState<number[] | null>(initialTasksStatus);
    const [tasksType, setTasksType] = useState<number[] | null>(initialTasksType);
    const [tasksExecutor, setTasksExecutor] = useState<number[] | null>(initialTasksExecutor);
    const [tasksController, setTasksController] = useState<number[] | null>(
        initialTasksController,
    );
    const [tasksOwner, setTasksOwner] = useState<number[] | null>(initialTasksOwner);
    const [filterLeadId, setFilterLeadId] = useState<number | null>(initialFilterLeadId);
    const [filterObjectId, setFilterObjectId] = useState<number | null>(initialFilterObjectId);
    const [onlyPersonal, setOnlyPersonal] = useState(Boolean(initialOnlyPersonal));
    const [onlyMine, setOnlyMine] = useState(initialOnlyMine !== false);
    const [listRefreshNonce, setListRefreshNonce] = useState(0);

    const prevListData = useRef<string | null>(null);
    const scopeLeadId = leadId ? Number(leadId) : null;
    const scopeObjectId = objectId ? Number(objectId) : null;
    const canFilterRelated = !scopeLeadId && !scopeObjectId;

    const triggerListRefresh = useCallback(() => {
        setListRefreshNonce((nonce) => nonce + 1);
    }, []);

    const triggerListReset = useCallback(() => {
        setTasksOrder('');
        setTasksDesc(false);
        setTasksStatus(null);
        setTasksType(null);
        setTasksExecutor(null);
        setTasksController(null);
        setTasksOwner(null);
        setFilterLeadId(null);
        setFilterObjectId(null);
        setOnlyPersonal(false);
        setOnlyMine(true);
        setPage(1);
        prevListData.current = null;
        setListRefreshNonce((nonce) => nonce + 1);
    }, []);

    const fetchTasks = async (force = false) => {
        if (!enabled) return;

        const requestLeadId = scopeLeadId || (canFilterRelated && !onlyPersonal ? filterLeadId : null);
        const requestObjectId =
            scopeObjectId || (canFilterRelated && !onlyPersonal ? filterObjectId : null);

        const listData: GetTaskListParamsType = {
            page: page < 0 ? 1 : page || 1,
            onPage,
            order: `${tasksOrder ? `order by ${tasksOrder} ` : ''}${tasksDesc ? 'DESC' : ''}`,
            search: '',
            status: tasksStatus,
            taskType: tasksType,
            executor: tasksExecutor,
            controller: tasksController,
            owner: tasksOwner,
            leadId: requestLeadId || undefined,
            objectId: requestObjectId || undefined,
            ...(canFilterRelated && onlyPersonal ? { onlyPersonal: true } : {}),
            ...(canFilterRelated && canViewAllTasks(auth?.user) && onlyMine
                ? { onlyMine: true }
                : {}),
            ...(noShowOlderTask ? { noShowOlderTask: true } : {}),
            ...(noShowDeletedTask ? { noShowDeletedTask: true } : {}),
        };

        const listKey = JSON.stringify({ ...listData, listRefreshNonce });
        if (!force && prevListData.current === listKey) return;
        prevListData.current = listKey;

        setShowSpinner(true);
        const res = await apiTask.getList(listData);
        setShowSpinner(false);

        if (!res) {
            showToast(t('messError'), 'danger');
            return;
        }

        setTasks(res.tasks || []);
        setPaginator(res.paginator || null);
    };

    useEffect(() => {
        if (!enabled) {
            prevListData.current = null;
            return;
        }
        fetchTasks();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        enabled,
        page,
        onPage,
        tasksOrder,
        tasksDesc,
        tasksStatus,
        tasksType,
        tasksExecutor,
        tasksController,
        tasksOwner,
        filterLeadId,
        filterObjectId,
        onlyPersonal,
        onlyMine,
        scopeLeadId,
        scopeObjectId,
        noShowOlderTask,
        noShowDeletedTask,
        listRefreshNonce,
    ]);

    useEffect(() => {
        if (!enabled) return;
        setPage(1);
        prevListData.current = null;
    }, [enabled, scopeLeadId, scopeObjectId, noShowOlderTask, noShowDeletedTask]);

    return (
        <TaskContext.Provider
            value={{
                tasks,
                paginator,
                showSpinner,
                page,
                setPage,
                onPage,
                setOnPage,
                tasksOrder,
                setTasksOrder,
                tasksDesc,
                setTasksDesc,
                tasksStatus,
                setTasksStatus,
                tasksType,
                setTasksType,
                tasksExecutor,
                setTasksExecutor,
                tasksController,
                setTasksController,
                tasksOwner,
                setTasksOwner,
                filterLeadId,
                setFilterLeadId,
                filterObjectId,
                setFilterObjectId,
                onlyPersonal,
                setOnlyPersonal,
                onlyMine,
                setOnlyMine,
                leadId: scopeLeadId,
                objectId: scopeObjectId,
                canFilterRelated,
                listRefreshNonce,
                triggerListRefresh,
                triggerListReset,
                fetchTasks,
            }}
        >
            {children}
        </TaskContext.Provider>
    );
};

export default TaskContext;
